import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trainingApi } from '../services/api';

function ProgressTracking() {
  const [searchParams] = useSearchParams();
  const pmIdFromQuery = searchParams.get('pm');
  
  const [pms, setPms] = useState([]);
  const [selectedPM, setSelectedPM] = useState(pmIdFromQuery || '');
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPMs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPM) {
      fetchProgress(selectedPM);
    }
  }, [selectedPM]);

  const fetchPMs = async () => {
    try {
      const response = await trainingApi.getDashboard();
      if (response.success) {
        setPms(response.data.pms);
        if (pmIdFromQuery && response.data.pms.length > 0) {
          setSelectedPM(pmIdFromQuery);
        } else if (response.data.pms.length > 0 && !selectedPM) {
          setSelectedPM(response.data.pms[0].pm_id.toString());
        }
      }
    } catch (err) {
      console.error('Error fetching PMs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async (pmId) => {
    try {
      const response = await trainingApi.getProgress(pmId);
      if (response.success) {
        setProgress(response.data);
      }
    } catch (err) {
      console.error('Error fetching progress:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Not Started': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const selectedPMData = pms.find(pm => pm.pm_id.toString() === selectedPM);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Progress Tracking</h1>
        <p className="text-gray-600 mt-2">Detailed timeline view of training progress</p>
      </div>

      {/* PM Selector */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Project Manager</label>
        <select
          value={selectedPM}
          onChange={(e) => setSelectedPM(e.target.value)}
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Choose a PM...</option>
          {pms.map(pm => (
            <option key={pm.pm_id} value={pm.pm_id}>
              {pm.pm_name} - {pm.email}
            </option>
          ))}
        </select>
      </div>

      {/* PM Overview */}
      {selectedPMData && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg p-8 text-white mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="h-20 w-20 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-3xl font-bold">
                {selectedPMData.pm_name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div>
              <h2 className="text-3xl font-bold">{selectedPMData.pm_name}</h2>
              <p className="text-blue-100">{selectedPMData.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-blue-200 text-sm">Target Certification</p>
              <p className="text-2xl font-bold">{selectedPMData.certification_target || 'None'}</p>
            </div>
            <div>
              <p className="text-blue-200 text-sm">Modules Completed</p>
              <p className="text-2xl font-bold">{selectedPMData.completed_modules} / {selectedPMData.total_modules}</p>
            </div>
            <div>
              <p className="text-blue-200 text-sm">Overall Progress</p>
              <p className="text-2xl font-bold">{selectedPMData.avg_percent_complete || 0}%</p>
            </div>
            <div>
              <p className="text-blue-200 text-sm">Overdue Items</p>
              <p className="text-2xl font-bold">{selectedPMData.overdue_count}</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Timeline */}
      {progress.length > 0 ? (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Module Progress Timeline</h2>
          </div>
          <div className="p-6">
            <div className="space-y-8">
              {progress.map((item, idx) => (
                <div key={item.progress_id} className="relative pl-8 pb-8 border-l-2 border-gray-200 last:border-l-0 last:pb-0">
                  <div className="absolute left-0 top-0 -ml-3 w-6 h-6 rounded-full bg-blue-600 border-4 border-white"></div>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                            {item.module_code}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">{item.module_title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{item.module_category}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Completion</span>
                        <span className="text-sm font-semibold text-gray-900">{item.percent_complete}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${
                            item.percent_complete === 100 ? 'bg-green-500' :
                            item.percent_complete > 0 ? 'bg-blue-600' :
                            'bg-gray-300'
                          }`}
                          style={{ width: `${item.percent_complete}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Started</p>
                        <p className="font-semibold text-gray-900">
                          {item.assigned_date ? new Date(item.assigned_date).toLocaleDateString() : 'Not started'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Target Date</p>
                        <p className={`font-semibold ${
                          item.due_date && new Date(item.due_date) < new Date() && item.status !== 'Completed'
                            ? 'text-red-600'
                            : 'text-gray-900'
                        }`}>
                          {item.due_date ? new Date(item.due_date).toLocaleDateString() : 'No deadline'}
                        </p>
                      </div>
                      {item.completed_date && (
                        <div>
                          <p className="text-gray-600">Completed</p>
                          <p className="font-semibold text-green-600">
                            {new Date(item.completed_date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-600">Quiz Score</p>
                        {item.quiz_score !== null && item.quiz_score !== undefined ? (
                          <p className={`font-semibold ${item.quiz_score >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                            {Math.round(item.quiz_score)}%
                          </p>
                        ) : (
                          <p className="text-gray-400">N/A</p>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    {item.notes && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          <strong>Notes:</strong> {item.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : selectedPM ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No progress records</h3>
          <p className="mt-1 text-sm text-gray-500">This PM hasn't been assigned any modules yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Select a PM</h3>
          <p className="mt-1 text-sm text-gray-500">Choose a project manager to view their training progress.</p>
        </div>
      )}
    </div>
  );
}

export default ProgressTracking;
